import React from "react";

import { presentTypes, shouldShowTypeTabs } from "./captureDrawerScopes";
import { captureTypeLabel, typeCounts, FILTER_ALL } from "../Capture.helpers";
// SHARED capture styleds (composed, never hand-rolled — G-21): the identical
// chip row the Capture inbox toolbar renders.
import { Chips, Chip, ChipCount } from "../styles";

/**
 * CaptureDrawerTypeTabs — "if multiple types of media are available in the
 * Edit-Page Capture, display tabs at the top to filter by media type" (QA-77,
 * Gordon verbatim).
 *
 * Renders NOTHING when the visible list holds fewer than two distinct types:
 * with one type every tab selects the same rows, so the row would be chrome
 * that costs vertical space in a 380px-wide pane.
 *
 * Only the types actually PRESENT get a tab. A tab for a type the user does not
 * own would either read `0` or, worse, read `0` while the type exists on a page
 * that has not loaded — the measured chip-count defect of 2026-07-30. Deriving
 * the tabs from the rows in hand makes that impossible by construction.
 *
 * Props:
 *   items    the rows the pane is showing (already scope-filtered)
 *   type     the active type, or FILTER_ALL
 *   onType   (type) => void
 */
const CaptureDrawerTypeTabs = ({ items = [], type = FILTER_ALL, onType }) => {
  if (!shouldShowTypeTabs(items)) return null;

  const types = presentTypes(items);
  const counts = typeCounts(items);

  return (
    <Chips role="group" aria-label="Filter by media type" data-testid="capture-type-tabs">
      <Chip
        type="button"
        active={type === FILTER_ALL}
        aria-pressed={type === FILTER_ALL}
        onClick={() => onType?.(FILTER_ALL)}
        data-testid="capture-type-tab-all"
      >
        All
        <ChipCount active={type === FILTER_ALL}>{counts[FILTER_ALL]}</ChipCount>
      </Chip>
      {types.map((t) => (
        <Chip
          key={t}
          type="button"
          active={type === t}
          aria-pressed={type === t}
          onClick={() => onType?.(t)}
          data-testid={`capture-type-tab-${t}`}
        >
          {captureTypeLabel(t)}
          <ChipCount active={type === t}>{counts[t]}</ChipCount>
        </Chip>
      ))}
    </Chips>
  );
};

export default CaptureDrawerTypeTabs;
